import { useState, useEffect } from 'react';
import { UserRound, UserPlus, ShieldCheck, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import useDebounce from '../hooks/useDebounce';

import { api } from '../services/client';
import { useTranslation } from '../i18n';
import useModal from '../hooks/useModal';
import useConfirmDialog from '../hooks/useConfirmDialog';
import {
  useAssignRoomAdmin,
  useRemoveRoomMember,
} from '../hooks/queries/useSpaceQueries';
import { IPaginatedRoomMembers, IRoomDetail } from '../types';
import { getImageUrl } from '../utils/imageUrl';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import MemberListLayout, { IMemberRow } from './shared/MemberListLayout';
import AddRoomMemberDialog from './AddRoomMemberDialog';

interface IChatMemberListProps {
  roomId: string;
  onBack: () => void;
}

const ChatMemberList = ({ roomId, onBack }: IChatMemberListProps) => {
  const { t } = useTranslation();
  const [roomInfo, setRoomInfo] = useState<IRoomDetail | null>(null);
  const [members, setMembers] = useState<IMemberRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const addMemberDialog = useModal();
  const confirmDialog = useConfirmDialog();
  const updateAdmins = useAssignRoomAdmin();
  const removeMember = useRemoveRoomMember();
  const limit = 20;

  useEffect(() => {
    api
      .get<IRoomDetail>(`/backoffice/rooms/${roomId}/detail`)
      .then(setRoomInfo)
      .catch(console.error);
  }, [roomId]);

  const loadMembers = async () => {
    setLoading(true);

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (search) params.set('search', search);
      if (roleFilter !== 'all') params.set('role', roleFilter);

      const data = await api.get<IPaginatedRoomMembers>(
        `/backoffice/rooms/${roomId}/members-paginated?${params}`,
      );
      setMembers(
        data.members.map((m) => ({
          ...m,
          lastActiveAt: m.lastActiveAt ?? null,
        })),
      );
      setTotal(data.total);
      setTotalPages(data.totalPages);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMembers();
  }, [roomId, page, roleFilter]);

  useDebounce(
    () => {
      setPage(1);
      loadMembers();
    },
    300,
    [search],
  );

  const adminCount = roomInfo?.adminMembers?.length || 0;
  const activeCount = roomInfo?.memberCount || total;

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'owner':
        return (
          <Badge variant="info" className="rounded-full">
            {t('chatroom.role_owner')}
          </Badge>
        );
      case 'space_admin':
        return (
          <Badge variant="purple" className="rounded-full">
            {t('chatroom.role_space_admin')}
          </Badge>
        );
      case 'admin':
        return (
          <Badge variant="purple" className="rounded-full">
            {t('chatroom.role_admin')}
          </Badge>
        );
      default:
        return (
          <Badge variant="muted" className="rounded-full">
            {t('chatroom.role_member')}
          </Badge>
        );
    }
  };

  return (
    <>
      <MemberListLayout
        onBack={onBack}
        backLabel={t('chatroom.back_to_detail')}
        headerIcon={
          <div className="w-12 h-12 rounded-lg bg-purple-500 flex items-center justify-center text-white overflow-hidden shrink-0">
            {roomInfo?.avatar ? (
              <img
                src={getImageUrl(roomInfo.avatar)!}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <MessageCircle className="w-6 h-6" />
            )}
          </div>
        }
        title={`${roomInfo?.name || '—'} - ${t('chatroom.member_list_title')}`}
        subtitle={t('chatroom.total_count', { count: total.toString() })}
        headerAction={
          <Button
            variant="default"
            onClick={() => addMemberDialog.open()}
            className="inline-flex items-center gap-1.5"
          >
            <UserPlus className="w-4 h-4" /> {t('spaces.add_member')}
          </Button>
        }
        stats={[
          {
            label: t('chatroom.total_members'),
            value: (roomInfo?.memberCount || total).toString(),
            icon: <UserRound className="w-8 h-8 text-blue-600" />,
            bg: '',
          },
          {
            label: t('chatroom.admin_count'),
            value: adminCount.toString(),
            icon: <ShieldCheck className="w-8 h-8 text-purple-600" />,
            bg: '',
          },
          {
            label: t('chatroom.active_members'),
            value: activeCount.toString(),
            icon: <UserRound className="w-8 h-8 text-green-600" />,
            bg: '',
          },
        ]}
        searchPlaceholder={t('chatroom.search_member_placeholder')}
        search={search}
        onSearchChange={setSearch}
        roleFilter={roleFilter}
        onRoleFilterChange={(role) => {
          setRoleFilter(role);
          setPage(1);
        }}
        filterLabels={{
          all: t('chatroom.filter_all'),
          admin: t('chatroom.filter_admin'),
          member: t('chatroom.filter_member'),
        }}
        members={members}
        loading={loading}
        showMessageCount
        getRoleBadge={getRoleBadge}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        onViewUser={(member) => {
          window.location.href = `/users/${member.userId}`;
        }}
        onToggleAdmin={async (member) => {
          const isAdmin = member.role === 'admin';
          const name = member.user?.displayName || member.user?.username || '';
          const msg = isAdmin
            ? t('members.confirm_remove_admin', { name })
            : t('members.confirm_set_admin', { name });

          confirmDialog.show(msg, async () => {
            try {
              const allMembers = await api.get<
                { userId: string; role: string }[]
              >(`/backoffice/rooms/${roomId}/members`);
              const currentAdminIds = allMembers
                .filter((m) => m.role === 'admin')
                .map((m) => m.userId);
              const newIds = isAdmin
                ? currentAdminIds.filter((aid) => aid !== member.userId)
                : [...currentAdminIds, member.userId];

              updateAdmins.mutate(
                { roomId, userIds: newIds },
                { onSuccess: () => loadMembers() },
              );
            } catch (e: any) {
              toast.error(e.message || t('common.operation_failed'));
            }
          });
        }}
        onRemoveMember={async (member) => {
          const name = member.user?.displayName || member.user?.username || '';

          confirmDialog.show(
            t('members.confirm_remove_member', { name }),
            async () => {
              removeMember.mutate(
                { roomId, userId: member.userId },
                { onSuccess: () => loadMembers() },
              );
            },
          );
        }}
      />
      <AddRoomMemberDialog
        isOpen={addMemberDialog.isOpen}
        onClose={() => {
          addMemberDialog.close();
          loadMembers();
        }}
        roomId={roomId}
        roomName={roomInfo?.name || ''}
        spaceId={roomInfo?.space?.id}
      />

      {/* Confirm Dialog */}
      <Dialog
        open={confirmDialog.isOpen}
        onOpenChange={(open) => !open && confirmDialog.cancel()}
      >
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>{t('common.confirm')}</DialogTitle>
            <DialogDescription>{confirmDialog.message}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={confirmDialog.cancel}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDialog.confirm}
              disabled={confirmDialog.isLoading}
            >
              {confirmDialog.isLoading
                ? t('common.saving')
                : t('common.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ChatMemberList;
